import React, { useState } from "react";
import { FileEdit, Save, Trash2, UploadCloud } from "lucide-react";
import Dropzone from "react-dropzone";
import { useFormik } from "formik";
import { videoInitialValues, videoValidationSchema } from "./validationSchema";
import { SubSection, useFormContext } from "context/BlogContext";
import DeleteModal from "Common/DeleteModal";
import { createSelector, original } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { generateUniqueId } from "utils/uuid";

interface VideoProps {
  sectionOrder: number;
  subSectionOrder: number;
  handleSaveSubSection: (
    sectionOrder: number,
    subSectionInfo: SubSection
  ) => void;
  handleEditSubSection: (sectionOrder: number, subSection: SubSection) => void;
  subSection: SubSection;
  handleDeleteSubSection: (subsectionOrder: number) => void;
}

const Video: React.FC<VideoProps> = ({
  sectionOrder,
  subSectionOrder,
  handleSaveSubSection,
  handleEditSubSection,
  subSection,
  handleDeleteSubSection,
}) => {
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [isCancel, setIsCancel] = useState(false);

  const { setBlogMedia, setDeletedMedia } = useFormContext();

  const selectDataList = createSelector(
    (state: any) => state.Blog,
    (state) => ({
      isLoading: state.isLoading,
    })
  );

  const { isLoading } = useSelector(selectDataList);

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    initialValues: videoInitialValues(subSection),
    validationSchema: videoValidationSchema,
    onSubmit: (values) => {
      handleSaveSubSection(sectionOrder, {
        mediaId: values.videoId,
        title: values.title,
        type: "video",
        attrs: { src: values.src, caption: values.caption },
        isEditing: false,
        order: subSectionOrder,
      });
    },
  });

  const isBlobUrl = (url: string) => {
    return url.startsWith("blob:");
  };

  const handleAcceptVideo = (files: any) => {
    const video = files[0];
    if (video) {
      const previewUrl = URL.createObjectURL(video);

      const isBlob = isBlobUrl(validation.values.src);
      if (!isBlob) {
        setDeletedMedia([validation.values.src]);
      }
      const videoId = generateUniqueId();
      const enhancedFile = Object.assign(video, {
        // id: videoId,
        preview: previewUrl,
        file: new Blob([video], { type: video.type }),
        blobName: video.name,
      });
      const key = `videos${sectionOrder}ss${subSectionOrder}`;
      setBlogMedia((prev: any) => ({
        ...prev,
        [videoId]: enhancedFile, // Store the updated file object inside coverImage
      }));
      setSelectedVideo({ preview: previewUrl, name: video.name });
      validation.setFieldValue("src", previewUrl);
      validation.setFieldValue("videoId", videoId); // Save videoId
    }
  };

  // Handle delete confirmation
  const handleDelete = () => {
    handleDeleteSubSection(subSection.order);
    setDeleteModal(false);
  };

  const handleCancelSubSection = () => {
    handleSaveSubSection(sectionOrder, {
      ...subSection,
      isEditing: true,
    });
    validation.resetForm();
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    validation.handleSubmit();
  };

  return (
    <div>
      <DeleteModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete}
        message="Are you sure you want to remove this subsection?"
      />
      {subSection.isEditing ? (
        <form>
          <div className="xl:col-span-6 pt-5">
            <label
              htmlFor={`subTitle`}
              className="inline-block mb-2 text-base font-medium"
            >
              Title
            </label>
            <input
              type="text"
              id="title"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Title"
              name="title"
              onChange={validation.handleChange} // Bind Formik onChange
              value={validation.values.title} // Bind Formik value
            />
          </div>
          <div className="pt-5">
            <label
              htmlFor="image"
              className="inline-block mb-2 text-base font-medium"
            >
              Upload Video
            </label>
            <Dropzone
              onDrop={(acceptfiles) => {
                handleAcceptVideo(acceptfiles);
              }}
              accept={{
                "video/*": [], // Allow all video types
              }}
              // maxFiles={1} // Limit to one video file
            >
              {({ getRootProps, getInputProps }: any) => (
                <div
                  className="flex w-full items-center justify-center bg-white border border-dashed rounded-md cursor-pointer dropzone border-slate-300 dropzone2 dark:bg-zink-700 dark:border-zink-500"
                  {...getRootProps()}
                >
                  <input {...getInputProps()} />
                  {validation.values.src ? (
                    <div>
                      <div className="rounded border-slate-200 dark:border-zink-500">
                        <div className="p-2 text-center">
                          <div>
                            <div className="p-2 mx-auto rounded-md h-[200px] bg-slate-100 dark:bg-zink-600">
                              <iframe
                                className="w-full rounded-md aspect-video"
                                title={selectedVideo?.name}
                                src={validation.values.src}
                              ></iframe>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full py-5 text-lg text-center dz-message needsclick">
                      <div className="mb-3">
                        <UploadCloud className="block size-12 mx-auto text-slate-500 fill-slate-200 dark:text-zink-200 dark:fill-zink-500"></UploadCloud>
                      </div>
                      <h5 className="mb-0 font-normal text-slate-500 text-15">
                        Drag and drop your files or <a href="#!">browse</a> your
                        files
                      </h5>
                    </div>
                  )}
                </div>
              )}
            </Dropzone>
          </div>
          <div className="pt-5">
            <label
              htmlFor="caption"
              className="inline-block mb-2 text-base font-medium"
            >
              Caption
            </label>
            <input
              type="text"
              id="caption"
              name="caption"
              placeholder="Caption"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              value={validation.values.caption}
              onChange={validation.handleChange}
            />
          </div>
        </form>
      ) : (
        // If not editing, show the preview
        <div className="w-full">
          {subSection.title && (
            <h6 className="mb-2 text-15">{subSection.title}</h6>
          )}
          <div className="pt-3 h-[250px] flex justify-center">
            <iframe
              className="rounded-md h-full w-auto aspect-video"
              title="Embedded YouTube Video"
              src={validation.values.src}
            ></iframe>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-x-2.5 pt-5">
        {subSection.isEditing ? (
          <div className="flex gap-x-3">
            {isCancel && (
              <button
                type="button"
                className={`${
                  isLoading ? "cursor-not-allowed opacity-50" : ""
                } text-red-500`}
                onClick={() => handleCancelSubSection()}
                disabled={isLoading}
              >
                <div className="flex align-center">
                  <span className="ml-1">Cancel</span>
                </div>
              </button>
            )}
            <button
              type="button"
              className={`text-green-500 ${
                isLoading
                  ? "cursor-not-allowed opacity-50"
                  : "hover:text-white hover:bg-green-600"
              } bg-green-100 btn focus:text-white focus:bg-green-600 focus:ring focus:ring-green-100 active:text-white active:bg-green-600 active:ring active:ring-green-100 dark:bg-green-500/20 dark:text-green-400 dark:hover:bg-green-500 dark:hover:text-white dark:focus:bg-green-500 dark:focus:text-white dark:active:bg-green-500 dark:active:text-white dark:ring-green-400/20`}
              onClick={(e) => handleSubmit(e)}
              disabled={isLoading}
            >
              <div className="flex align-center">
                <Save className="size-4"></Save>
                <span className="ml-1">Save</span>
              </div>
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={`text-custom-500 ${
              isLoading
                ? "cursor-not-allowed opacity-50"
                : "hover:text-white hover:bg-custom-600"
            } btn bg-custom-100 focus:text-white focus:bg-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:ring active:ring-custom-100 dark:bg-custom-500/20 dark:text-custom-500 dark:hover:bg-custom-500 dark:hover:text-white dark:focus:bg-custom-500 dark:focus:text-white dark:active:bg-custom-500 dark:active:text-white dark:ring-custom-400/20`}
            onClick={() => {
              handleEditSubSection(sectionOrder, subSection);
              setIsCancel(true);
            }} // Switch to edit mode when clicked
            disabled={isLoading}
          >
            <div className="flex align-center">
              <FileEdit className="size-4"></FileEdit>
              <span className="ml-1">Edit</span>
            </div>
          </button>
        )}
        <button
          type="button"
          className={`${
            isLoading ? "cursor-not-allowed opacity-50" : ""
          } text-red-500`}
          onClick={() => setDeleteModal(true)}
          disabled={isLoading}
        >
          <div className="flex align-center">
            <Trash2 className="size-4 text-red-500"></Trash2>
            <span className="ml-1">Delete</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default Video;
